/**
 * LangSmith Exporter for Mastra Tracing
 *
 * This exporter sends observability data to LangSmith
 * Root spans become top-level LangSmith RunTrees (no trace wrapper).
 * Events are handled as zero-duration RunTrees with matching start/end times.
 */

import type { AnyExportedSpan, ModelGenerationAttributes, ScoreEvent, SpanErrorInfo } from '@mastra/core/observability';
import { SpanType } from '@mastra/core/observability';
import { omitKeys } from '@mastra/core/utils';
import { TrackingExporter } from '@mastra/observability';
import type { TraceData, TrackingExporterConfig } from '@mastra/observability';
import type { ClientConfig, RunTreeConfig } from 'langsmith';
import { Client, RunTree } from 'langsmith';
import type { KVMap } from 'langsmith/schemas';
import type { LangSmithMetadataInput } from './helpers';
import { formatUsageMetrics } from './metrics';

export interface LangSmithExporterConfig extends ClientConfig, TrackingExporterConfig {
  /** LangSmith client instance */
  client?: Client;
  /**
   * The name of the LangSmith project to send traces to.
   * Overrides the LANGCHAIN_PROJECT environment variable.
   * If neither is set, traces are sent to the "default" project.
   */
  projectName?: string;
  /**
   * Maximum number of `spanId → langsmithRunId` mappings to retain for resolving
   * `onScoreEvent` lookups. Older entries are evicted in LRU order when the cap
   * is exceeded so long-running processes do not grow unbounded.
   * Defaults to 10000.
   */
  runIdCacheMaxEntries?: number;
}

const DEFAULT_RUN_ID_CACHE_MAX_ENTRIES = 10_000;

type LangSmithRoot = undefined;
type LangSmithSpan = RunTree;
type LangSmithEvent = RunTree;
type LangSmithMetadata = LangSmithMetadataInput;
type LangSmithTraceData = TraceData<LangSmithRoot, LangSmithSpan, LangSmithEvent, LangSmithMetadata>;

// Default span type for all spans
const DEFAULT_SPAN_TYPE = 'chain';

// Exceptions to the default mapping
const SPAN_TYPE_EXCEPTIONS: Partial<Record<SpanType, 'llm' | 'tool' | 'chain'>> = {
  [SpanType.MODEL_GENERATION]: 'llm',
  [SpanType.TOOL_CALL]: 'tool',
  [SpanType.MCP_TOOL_CALL]: 'tool',
  [SpanType.PROVIDER_TOOL_CALL]: 'tool',
  [SpanType.WORKFLOW_CONDITIONAL_EVAL]: 'chain',
  [SpanType.WORKFLOW_WAIT_EVENT]: 'chain',
};

// Mapping function - returns valid LangSmith span types
function mapSpanType(spanType: SpanType): 'llm' | 'tool' | 'chain' {
  return SPAN_TYPE_EXCEPTIONS[spanType] ?? DEFAULT_SPAN_TYPE;
}

function isKVMap(value: unknown): value is KVMap {
  return value != null && typeof value === 'object' && !Array.isArray(value) && !(value instanceof Date);
}

export class LangSmithExporter extends TrackingExporter<
  LangSmithRoot,
  LangSmithSpan,
  LangSmithEvent,
  LangSmithMetadata,
  LangSmithExporterConfig
> {
  override name = 'langsmith';
  #client: Client | undefined;

  /**
   * Maps Mastra `span.id` to the runId LangSmith allocated when the corresponding
   * RunTree was created. LangSmith's `createFeedback` requires the LangSmith runId,
   * not the Mastra span id, so scores submitted via `onScoreEvent` look up the
   * LangSmith runId here before calling the API.
   *
   * Bounded LRU keyed by Mastra span id — entries are evicted oldest-first when
   * size exceeds `#runIdCacheMaxEntries` so the cache cannot grow without bound.
   */
  #langsmithRunIdBySpanId = new Map<string, string>();
  #runIdCacheMaxEntries: number;

  constructor(config: LangSmithExporterConfig = {}) {
    // Resolve env vars BEFORE calling super (config is readonly in base class)
    const apiKey = config.apiKey ?? process.env.LANGSMITH_API_KEY;

    super({
      ...config,
      apiKey,
    });

    this.#runIdCacheMaxEntries = Math.max(1, config.runIdCacheMaxEntries ?? DEFAULT_RUN_ID_CACHE_MAX_ENTRIES);

    if (!apiKey) {
      this.setDisabled(`Missing required credentials (apiKey: ${!!apiKey})`);
      return;
    }

    this.#client = config.client ?? new Client(this.config);
  }

  /** Look up the LangSmith runId for a Mastra span id, refreshing its LRU position on hit. */
  #getLangsmithRunId(spanId: string): string | undefined {
    const runId = this.#langsmithRunIdBySpanId.get(spanId);
    if (runId === undefined) return undefined;
    // Re-insert to mark as most-recently-used (Map preserves insertion order).
    this.#langsmithRunIdBySpanId.delete(spanId);
    this.#langsmithRunIdBySpanId.set(spanId, runId);
    return runId;
  }

  /** Remember the LangSmith runId for a Mastra span id, evicting oldest entries when full. */
  #rememberLangsmithRunId(spanId: string, runId: string): void {
    if (this.#langsmithRunIdBySpanId.has(spanId)) {
      this.#langsmithRunIdBySpanId.delete(spanId);
    }
    this.#langsmithRunIdBySpanId.set(spanId, runId);
    while (this.#langsmithRunIdBySpanId.size > this.#runIdCacheMaxEntries) {
      const oldest = this.#langsmithRunIdBySpanId.keys().next().value;
      if (oldest === undefined) break;
      this.#langsmithRunIdBySpanId.delete(oldest);
    }
  }

  /**
   * Flush pending trace batches to LangSmith.
   * The LangSmith Client internally batches API calls; this ensures
   * all queued runs are sent before the process exits or the flush
   * caller continues.
   */
  protected override async _flush(): Promise<void> {
    if (this.#client) {
      await this.#client.awaitPendingTraceBatches();
    }
  }

  protected override async _postShutdown(): Promise<void> {
    this.#langsmithRunIdBySpanId.clear();
  }

  async onScoreEvent(event: ScoreEvent): Promise<void> {
    if (!this.#client) return;

    const { score } = event;
    if (!score.spanId) {
      this.logger.warn('LangSmith exporter: dropping score with no spanId; trace-level scoring is not yet supported', {
        scorerId: score.scorerId,
      });
      return;
    }

    const langsmithRunId = this.#getLangsmithRunId(score.spanId);
    if (!langsmithRunId) {
      this.logger.warn(
        'LangSmith exporter: dropping score for a span that was not previously emitted to LangSmith ' +
          '(span_started must be processed before submitting a score for it)',
        {
          traceId: score.traceId,
          spanId: score.spanId,
          scorerId: score.scorerId,
        },
      );
      return;
    }

    const key = score.scorerName ?? score.scorerId;

    try {
      await this.#client.createFeedback(langsmithRunId, key, {
        score: score.score,
        ...(score.reason ? { comment: score.reason } : {}),
        feedbackId: score.scoreId,
        ...(score.scoreTraceId ? { sourceRunId: score.scoreTraceId } : {}),
        sourceInfo: {
          // User-supplied metadata is spread first so the authoritative reserved
          // fields below cannot be overwritten by `scorerId` / `scoreSource` keys
          // a caller may have set inside metadata.
          ...(score.metadata ?? {}),
          scorerId: score.scorerId,
          ...(score.scoreSource ? { scoreSource: score.scoreSource } : {}),
        },
      });
    } catch (err) {
      this.logger.error('LangSmith exporter: Failed to submit feedback', {
        error: err,
        traceId: score.traceId,
        spanId: score.spanId,
        scorerId: score.scorerId,
      });
    }
  }

  protected override skipBuildRootTask = true;
  protected override async _buildRoot(_args: {
    span: AnyExportedSpan;
    traceData: LangSmithTraceData;
  }): Promise<LangSmithRoot | undefined> {
    throw new Error('Method not implemented.');
  }

  protected override async _buildSpan(args: {
    span: AnyExportedSpan;
    traceData: LangSmithTraceData;
  }): Promise<LangSmithSpan | undefined> {
    const { span, traceData } = args;

    const parent = span.isRootSpan ? undefined : traceData.getParent(args);

    if (!span.isRootSpan && !parent) {
      // parent doesn't exist and not creating rootSpan, return early data
      return;
    }

    const payload = {
      name: span.name,
      ...this.buildRunTreePayload(span, traceData, true),
    };

    const langSmithSpan = span.isRootSpan ? new RunTree(payload) : parent!.createChild(payload);

    if (langSmithSpan.id) {
      this.#rememberLangsmithRunId(span.id, langSmithSpan.id);
    }

    await langSmithSpan.postRun();
    return langSmithSpan;
  }

  protected override async _buildEvent(args: {
    span: AnyExportedSpan;
    traceData: LangSmithTraceData;
  }): Promise<LangSmithEvent | undefined> {
    const langSmithSpan = await this._buildSpan(args);

    if (!langSmithSpan) {
      // parent doesn't exist and not creating rootSpan, return early data
      return;
    }

    // use start-time as end-time to make an event span.
    await langSmithSpan.end({ endTime: args.span.startTime.getTime() });
    await langSmithSpan.patchRun();
    return langSmithSpan;
  }

  protected override async _updateSpan(args: { span: AnyExportedSpan; traceData: LangSmithTraceData }): Promise<void> {
    await this.handleSpanUpdateOrEnd({ ...args, isEnd: false });
  }

  protected override async _finishSpan(args: { span: AnyExportedSpan; traceData: LangSmithTraceData }): Promise<void> {
    await this.handleSpanUpdateOrEnd({ ...args, isEnd: true });
  }

  protected override async _abortSpan(args: {
    span: LangSmithSpan;
    traceData: LangSmithTraceData;
    reason: SpanErrorInfo;
  }): Promise<void> {
    const { span, reason } = args;
    span.error = reason.message;
    span.metadata = {
      ...span.metadata,
      errorDetails: reason,
    };
    await span.end();
    await span.patchRun();
  }

  private async handleSpanUpdateOrEnd(args: {
    span: AnyExportedSpan;
    traceData: LangSmithTraceData;
    isEnd: boolean;
  }): Promise<void> {
    const { span, traceData, isEnd } = args;

    const langSmithSpan = traceData.getSpan({ spanId: span.id });
    if (!langSmithSpan) {
      //update occurred before span start, return early data
      return;
    }

    const updatePayload = this.buildRunTreePayload(span, traceData);

    langSmithSpan.metadata = {
      ...langSmithSpan.metadata,
      ...updatePayload.metadata,
    };
    if (updatePayload.inputs != null) {
      langSmithSpan.inputs = updatePayload.inputs;
    }
    if (updatePayload.outputs != null) {
      langSmithSpan.outputs = updatePayload.outputs;
    }
    if (updatePayload.error != null) {
      langSmithSpan.error = updatePayload.error;
    }

    // Add new_token event for TTFT tracking on MODEL_GENERATION spans
    if (span.type === SpanType.MODEL_GENERATION) {
      const modelAttr = (span.attributes ?? {}) as ModelGenerationAttributes;
      if (modelAttr.completionStartTime !== undefined) {
        langSmithSpan.addEvent({
          name: 'new_token',
          time: modelAttr.completionStartTime.toISOString(),
        });
      }
    }

    if (isEnd) {
      // End the span with the correct endTime
      if (span.endTime) {
        await langSmithSpan.end({ endTime: span.endTime.getTime() });
      } else {
        await langSmithSpan.end();
      }
    }
    await langSmithSpan.patchRun();
  }

  /**
   * Find LangSmith vendor metadata by walking up the span hierarchy and merging.
   * Metadata is merged from ancestors with child values taking precedence over parent values.
   *
   * TODO(2.0): Extract shared `findVendorMetadata()` to base TrackingExporter class
   * and reuse here and in LangfuseExporter.findLangfusePrompt()
   */
  private findLangsmithMetadata(
    traceData: LangSmithTraceData,
    span: AnyExportedSpan,
  ): LangSmithMetadataInput | undefined {
    // Collect metadata from all ancestors (current span first, then parents)
    const metadataChain: LangSmithMetadataInput[] = [];
    let currentSpanId: string | undefined = span.id;

    while (currentSpanId) {
      const providerMetadata = traceData.getMetadata({ spanId: currentSpanId });
      if (providerMetadata) {
        metadataChain.push(providerMetadata);
      }
      currentSpanId = traceData.getParentId({ spanId: currentSpanId });
    }

    if (metadataChain.length === 0) {
      return undefined;
    }

    // Merge from ancestors to current span (parent values first, child values override)
    const merged: LangSmithMetadataInput = {};
    for (let i = metadataChain.length - 1; i >= 0; i--) {
      const meta = metadataChain[i]!;
      if (meta.projectName !== undefined) merged.projectName = meta.projectName;
      if (meta.sessionId !== undefined) merged.sessionId = meta.sessionId;
      if (meta.sessionName !== undefined) merged.sessionName = meta.sessionName;
    }

    this.logger.debug(`${this.name}: merged vendor metadata from hierarchy`, {
      traceId: span.traceId,
      spanId: span.id,
      metadataKeys: Object.keys(merged),
      ancestorCount: metadataChain.length,
    });

    return merged;
  }

  private buildRunTreePayload(
    span: AnyExportedSpan,
    traceData: LangSmithTraceData,
    isNew = false,
  ): Partial<RunTreeConfig> {
    // Extract vendor metadata from span hierarchy
    const vendorMetadata = this.findLangsmithMetadata(traceData, span);

    // Build metadata, omitting the langsmith vendor key
    const spanMetadata = span.metadata ? omitKeys(span.metadata, ['langsmith']) : {};

    const payload: Partial<RunTreeConfig> & { metadata: KVMap } = {
      client: this.#client,
      metadata: {
        mastra_span_type: span.type,
        ...spanMetadata,
      },
    };

    if (isNew) {
      payload.run_type = mapSpanType(span.type);
      payload.start_time = span.startTime.getTime();
    }

    // Add project name - vendor metadata takes precedence over config
    const projectName = vendorMetadata?.projectName ?? this.config.projectName;
    if (projectName) {
      payload.project_name = projectName;
    }

    // Add session info to metadata if provided via vendor metadata
    if (vendorMetadata?.sessionId) {
      payload.metadata.session_id = vendorMetadata.sessionId;
    }
    if (vendorMetadata?.sessionName) {
      payload.metadata.session_name = vendorMetadata.sessionName;
    }

    // Add tags for root spans
    if (span.isRootSpan && span.tags?.length) {
      payload.tags = span.tags;
    }

    // Core span data
    if (span.input !== undefined) {
      payload.inputs = isKVMap(span.input) ? span.input : { input: span.input };
    }

    if (span.output !== undefined) {
      payload.outputs = isKVMap(span.output) ? span.output : { output: span.output };
    }

    const attributes = (span.attributes ?? {}) as Record<string, any>;

    if (span.type === SpanType.MODEL_GENERATION) {
      const modelAttr = attributes as ModelGenerationAttributes;

      // See: https://docs.langchain.com/langsmith/log-llm-trace
      if (modelAttr.model !== undefined) {
        // Note - this should map to a model name recognized by LangSmith
        // eg “gpt-4o-mini”, “claude-3-opus-20240307”, etc.
        payload.metadata.ls_model_name = modelAttr.model;
      }

      // Provider goes to metadata (if provided by attributes)
      if (modelAttr.provider !== undefined) {
        // Note - this should map to a provider name recognized by
        // LangSmith eg “openai”, “anthropic”, etc.
        payload.metadata.ls_provider = modelAttr.provider;
      }

      // Usage/token info goes to metrics
      payload.metadata.usage_metadata = formatUsageMetrics(modelAttr.usage);

      // Model parameters go to metadata
      if (modelAttr.parameters !== undefined) {
        payload.metadata.modelParameters = modelAttr.parameters;
      }

      // Other LLM attributes go to metadata
      const otherAttributes = omitKeys(attributes, ['model', 'provider', 'usage', 'parameters', 'completionStartTime']);
      payload.metadata = {
        ...payload.metadata,
        ...otherAttributes,
      };
    } else {
      // For non-LLM spans, put all attributes in metadata
      payload.metadata = {
        ...payload.metadata,
        ...attributes,
      };
    }

    // Handle errors
    if (span.errorInfo) {
      payload.error = span.errorInfo.message;
      payload.metadata.errorDetails = span.errorInfo;
    }

    return payload;
  }
}
